import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn.js'

export interface OtpInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  /** Fired when the last box is filled, so the user need not hunt for Submit. */
  onComplete?: (value: string) => void
  disabled?: boolean
  invalid?: boolean
  autoFocus?: boolean
  /** Kiosk and POS need targets a thumb can hit. */
  size?: 'md' | 'touch'
  label?: string
}

/**
 * Six-box code entry for TOTP and SMS codes.
 *
 * Paste is deliberately supported: WCAG 2.2 (3.3.8, Accessible Authentication)
 * exists partly because blocking paste forces people to memorise and retype a
 * code, which is exactly what an authenticator app is meant to avoid.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  invalid,
  autoFocus,
  size = 'md',
  label = 'Verification code',
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const [focusedIndex, setFocusedIndex] = useState(0)

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
  }, [autoFocus])

  const setDigit = (index: number, digit: string) => {
    const next = value.split('')
    next[index] = digit
    const joined = next.join('').slice(0, length)
    onChange(joined)
    if (joined.length === length && !joined.includes('') && onComplete) {
      onComplete(joined)
    }
    return joined
  }

  const handleChange = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '')
    if (!digits) return

    if (digits.length > 1) {
      // A paste or a fast scanner: distribute across the boxes from here on.
      const next = (value.slice(0, index) + digits).slice(0, length)
      onChange(next)
      const target = Math.min(next.length, length - 1)
      refs.current[target]?.focus()
      if (next.length === length && onComplete) onComplete(next)
      return
    }

    const joined = setDigit(index, digits)
    if (index < length - 1) refs.current[index + 1]?.focus()
    else if (joined.length === length && onComplete) onComplete(joined)
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (value[index]) {
        setDigit(index, '')
      } else if (index > 0) {
        // Backspace on an empty box steps back and clears — what people expect.
        const next = value.split('')
        next[index - 1] = ''
        onChange(next.join(''))
        refs.current[index - 1]?.focus()
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      refs.current[index - 1]?.focus()
    } else if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault()
      refs.current[index + 1]?.focus()
    }
  }

  return (
    <div
      className="flex gap-2"
      role="group"
      aria-label={label}
      aria-invalid={invalid || undefined}
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          // Lets a browser or OS offer the code straight from the SMS.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          value={value[index] ?? ''}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${length}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => {
            setFocusedIndex(index)
            e.target.select()
          }}
          className={cn(
            'rounded-lg border bg-surface text-center font-identifier font-medium transition-colors',
            'focus-visible:border-primary disabled:opacity-50',
            size === 'touch' ? 'size-16 text-2xl' : 'size-11 text-lg',
            invalid ? 'border-danger' : 'border-border',
            focusedIndex === index && !invalid && 'border-primary',
          )}
        />
      ))}
    </div>
  )
}
